import { useState, useRef } from 'react';
import { Post } from '@/hooks/usePosts';
import { useCategories } from '@/hooks/useCategories';
import RichTextEditor from '@/components/base/RichTextEditor';

interface PostEditorProps {
  post?: Post;
  onSave: (post: Post) => void;
  onCancel: () => void;
}

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

function slugify(text: string) {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

export default function PostEditor({ post, onSave, onCancel }: PostEditorProps) {
  const { categories } = useCategories();
  const activeCategories = categories.filter(c => c.active);

  const [title, setTitle]         = useState(post?.title ?? '');
  const [subtitle, setSubtitle]   = useState(post?.subtitle ?? '');
  const [selectedCats, setSelectedCats] = useState<string[]>(
    post?.categories?.length ? post.categories : (activeCategories[0] ? [activeCategories[0].id] : [])
  );
  const [readTime, setReadTime]   = useState(post?.readTime ?? '5 min de leitura');
  const [featured, setFeatured]   = useState(post?.featured ?? false);
  const [published, setPublished] = useState(post?.published ?? true);
  const [image, setImage]         = useState(post?.image ?? '');
  const [bodyHtml, setBodyHtml]   = useState(post?.bodyHtml ?? '');
  const fileRef                   = useRef<HTMLInputElement>(null);

  const toggleCat = (id: string) => {
    setSelectedCats(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const handleImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setImage(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleSave = () => {
    if (!title.trim()) return;
    const savedPost: Post = {
      id: post?.id ?? generateId(),
      title: title.trim(),
      subtitle: subtitle.trim(),
      categories: selectedCats.length > 0 ? selectedCats : (activeCategories[0] ? [activeCategories[0].id] : []),
      readTime,
      featured,
      published,
      image,
      bodyHtml: bodyHtml.trim(),
      content: post?.content ?? [],
      createdAt: post?.createdAt ?? new Date().toISOString(),
      slug: post?.slug ?? slugify(title.trim()),
    };
    onSave(savedPost);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">
          {post ? 'Editar Artigo' : 'Novo Artigo'}
        </h2>
        <div className="flex items-center gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-foreground-600 border border-background-300 rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={!title.trim() || selectedCats.length === 0}
            className="px-4 py-2 text-sm bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-md transition-colors cursor-pointer whitespace-nowrap font-bold"
          >
            Salvar Artigo
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Fields */}
        <div className="lg:col-span-2 space-y-5">
          <div>
            <label className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-1.5">Título <span className="text-primary-500">*</span></label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Título do artigo..." className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-background-50" />
          </div>

          <div>
            <label className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-1.5">Subtítulo / Resumo</label>
            <textarea value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="Breve descrição do artigo..." rows={3} maxLength={500} className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-background-50 resize-none" />
            <p className="text-xs text-foreground-400 text-right mt-1">{subtitle.length}/500</p>
          </div>

          {/* Rich Text Editor - ÚNICO campo de conteúdo */}
          <div>
            <label className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-3">
              Conteúdo do Artigo
              <span className="font-normal text-foreground-400 normal-case tracking-normal ml-1">— escreva tudo aqui ou cole HTML direto</span>
            </label>
            <RichTextEditor
              value={bodyHtml}
              onChange={setBodyHtml}
              placeholder="Escreva o conteúdo completo do artigo aqui... Use a toolbar para formatar ou clique em HTML para colar código."
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          {/* Featured Image */}
          <div className="border border-background-200 rounded-lg p-4 bg-background-50">
            <label className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-3">Imagem Destacada</label>
            {image ? (
              <div className="space-y-2">
                <div className="w-full h-36 rounded-md overflow-hidden">
                  <img src={image} alt="preview" className="w-full h-full object-cover object-top" />
                </div>
                <button onClick={() => setImage('')} className="text-xs text-red-500 hover:text-red-700 cursor-pointer">Remover imagem</button>
              </div>
            ) : (
              <div className="space-y-2">
                <input type="text" placeholder="URL da imagem..." onBlur={e => setImage(e.target.value)} className="w-full border border-background-300 rounded px-3 py-2 text-xs focus:outline-none focus:border-primary-400 bg-white" />
                <div className="text-center text-foreground-400 text-xs">ou</div>
                <button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-background-300 rounded-md py-4 text-xs text-foreground-400 hover:border-primary-300 hover:text-primary-500 transition-colors cursor-pointer flex flex-col items-center gap-1">
                  <i className="ri-upload-2-line text-xl"></i> Upload de arquivo
                </button>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleImageFile} className="hidden" />
              </div>
            )}
          </div>

          {/* Categories */}
          <div className="border border-background-200 rounded-lg p-4 bg-background-50">
            <label className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-3">
              Categorias <span className="text-primary-500">*</span>
              <span className="font-normal text-foreground-400 normal-case tracking-normal ml-1">(selecione uma ou mais)</span>
            </label>
            {activeCategories.length === 0 ? (
              <p className="text-foreground-400 text-xs">Nenhuma categoria ativa. Crie uma na aba Categorias.</p>
            ) : (
              <div className="space-y-2">
                {activeCategories.map(cat => {
                  const checked = selectedCats.includes(cat.id);
                  return (
                    <label key={cat.id} className="flex items-center gap-3 cursor-pointer group">
                      <div
                        onClick={() => toggleCat(cat.id)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer ${
                          checked ? 'bg-primary-500 border-primary-500' : 'border-background-300 hover:border-primary-300'
                        }`}
                      >
                        {checked && <i className="ri-check-line text-white text-xs"></i>}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 flex items-center justify-center rounded bg-primary-100">
                          <i className={`${cat.icon} text-primary-500 text-xs`}></i>
                        </div>
                        <span className={`text-sm transition-colors ${checked ? 'text-primary-600 font-bold' : 'text-foreground-700 group-hover:text-foreground-950'}`}>
                          {cat.label}
                        </span>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            {selectedCats.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {selectedCats.map(id => {
                  const cat = activeCategories.find(c => c.id === id);
                  return cat ? (
                    <span key={id} className="inline-flex items-center gap-1 bg-primary-100 text-primary-600 text-xs font-bold px-2 py-0.5 rounded-full">
                      {cat.label}
                    </span>
                  ) : null;
                })}
              </div>
            )}
          </div>

          {/* Read Time */}
          <div className="border border-background-200 rounded-lg p-4 bg-background-50">
            <label className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-2">Tempo de Leitura</label>
            <input type="text" value={readTime} onChange={e => setReadTime(e.target.value)} placeholder="Ex: 5 min de leitura" className="w-full border border-background-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary-400 bg-white" />
          </div>

          {/* Options */}
          <div className="border border-background-200 rounded-lg p-4 bg-background-50 space-y-3">
            <label className="block text-xs font-bold text-foreground-700 uppercase tracking-wide mb-1">Opções</label>
            {[
              { label: 'Destacar no carrossel', value: featured, toggle: () => setFeatured(v => !v) },
              { label: 'Publicado', value: published, toggle: () => setPublished(v => !v) },
            ].map(opt => (
              <label key={opt.label} className="flex items-center gap-3 cursor-pointer">
                <div onClick={opt.toggle} className={`w-10 h-5 rounded-full relative transition-colors cursor-pointer ${opt.value ? 'bg-primary-500' : 'bg-background-300'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${opt.value ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
                <span className="text-sm text-foreground-700">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}